// Grid.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './Grid.css';

/**
 * Grid
 * 
 * A responsive grid component.
 * 
 * @param {object[]} items The items to display.
 * @param {Function} renderItem The render item function.
 * @param {Boolean} loading If the grid is loading.
 * @param {Number} minCols The minimum number of columns.
 * @param {Number} colWidth The column width.
 * 
 * @returns {ReactNode} Grid component.
 */
const Grid = ({ items, renderItem, loading, gap = "10px", minCols = 2, maxCols = Infinity, colWidth = 280 }) => {

    // ----- STATE ----- //

    // Columns

    const [columns, setColumns] = useState([]); // columns will be an array of arrays

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- REFS ----- //

    // References

    const containerRef = useRef(null);

    // ----- UTILITIES ----- //

    // Distribute items into columns

    const distributeItemsIntoColumns = (itemsArray, numCols) => {
        if (!itemsArray || itemsArray.length === 0) return [];
        const newColumns = Array.from({ length: numCols }, () => []);
        itemsArray.forEach((item, index) => {
            const colIndex = index % numCols;
            newColumns[colIndex].push(item);
        });
        return newColumns;
    };

    // ----- MEMOS ----- //

    // Calculate the number of columns based on container width

    const calculateColumns = useCallback((width) => {
        const estimatedCols = Math.floor(width / colWidth);
        return Math.min(maxCols, Math.max(minCols, estimatedCols));
    }, [minCols, maxCols, colWidth]);

    // ----- EFFECTS ----- //

    // Use ResizeObserver to detect changes in container width

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width;
                let numCols = calculateColumns(width);
                setColumns(distributeItemsIntoColumns(items, numCols));
            }
        });

        observer.observe(container);

        return () => {
            if (container) observer.unobserve(container);
        };
    }, [items, calculateColumns, isMobile]);

    // Initially, set up columns when items or container load
    useEffect(() => {
        if (!containerRef.current || !items || items.length === 0) return;
        const width = containerRef.current.getBoundingClientRect().width;
        let numCols = calculateColumns(width);
        if (isMobile) { // Hardcode 2 columns on mobile
            numCols = 2;
        }
        setColumns(distributeItemsIntoColumns(items, numCols));
    }, [items, calculateColumns, isMobile]);

    if (loading) {

        return <div className="grid-loading">Loading...</div>;
    }

    // ----- RENDER ----- //

    return (
        <div className="grid-container">

            {/* CONTENT */}
            <div
              className="grid-content"
              style={{
                    gap: gap,
                }}
              ref={containerRef}
            >

                {columns.map((colItems, colIndex) => (

                    <div
                      className="grid-column"
                      style={{
                            gap: gap,
                        }}
                      key={`column-${colIndex}`}
                    >
                        {colItems.map((item) => renderItem(item))}
                    </div>

                ))}

            </div >

        </div>
    );
};

// Dev Tools
Grid.displayName = 'Grid';

export default Grid;
